import { useCallback, useEffect, useRef, useState } from 'react'
import StageView, { type SelectionBox, type StageHandle } from './render/StageView'
import Rail from './ui/Rail'
import Dock from './ui/Dock'
import { useCoarsePointer } from './ui/bits'
import Inspector from './ui/Inspector'
import CategoryRail from './ui/CategoryRail'
import Quote from './ui/Quote'
import SignIn from './ui/SignIn'
import { OPEN_ACCOUNT } from './lib/account'
import Lapsed from './ui/Lapsed'
import { useAuth } from './store/useAuth'
import { syncNow } from './lib/sync'
import TopBar from './ui/TopBar'
import LeftNav from './ui/LeftNav'
import CanvasControls from './ui/CanvasControls'
import DrawHud from './ui/DrawHud'
import SelectionBadge from './ui/SelectionBadge'
import { IconClose, IconPhoto } from './ui/icons'
import { useEditor } from './store/useEditor'
import type { RenderStats } from './render/scene'
import { loadPhotoFile } from './lib/photo'
/* buildQuote was imported here to store a job's total on the summary. Nothing
 * in the app shows a price while this is a visual design tool, so nothing
 * computes one here; lib/pricing.ts is untouched. */
import { makeDemoPhoto } from './lib/demoPhoto'
import { listProjects, loadProject, makeThumb, migrateFromLocalStorage, saveProject } from './lib/db'
import Projects from './ui/Projects'
import Workspace from './ui/Workspace'
import ShareSheet from './ui/ShareSheet'
import MeasureSheet from './ui/MeasureSheet'
import TrialNotice from './ui/TrialNotice'
import NightDebug from './ui/NightDebug'
// The design package's tokens, verbatim, before anything reads them. They are
// declared on :root so both rooms can reach the ones they need; the workspace
// maps its own names onto them in workspace.css and the editor keeps its dark
// set in tokens.css.
import './styles/ds-tokens.css'
import './styles/app.css'
import './styles/dock-compact.css'
import './styles/quote-doc.css'
// The editor in daylight: light chrome, dark ground around the photograph.
// Redeclares the tokens on .app rather than overriding app.css.
import './styles/editor.css'
// Loaded last: the light workspace wins over the dark editor's rules wherever
// a screen carries the ws class, without app.css being edited to let it.
// See styles/workspace.css.
import './styles/workspace.css'

/** Sep 2, on the resume card. A plain date, never an instant. */
const SHORT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

const emptyStats: RenderStats = { totalBulbs: 0, totalFeet: null, perStrand: [] }

export default function App() {
  const [stats, setStats] = useState<RenderStats>(emptyStats)
  const [exporting, setExporting] = useState(false)
  const [dragging, setDragging] = useState(false)
  /*
   * On a phone the panel starts COLLAPSED.
   *
   * The panel is a side rail on a desk and a bottom sheet on a phone, and a
   * bottom sheet that opens full height covers the photograph - which on the
   * first run meant the app opened showing no house at all. The photo is the
   * product; the controls slide up over it when wanted.
   */
  const [panelOpen, setPanelOpen] = useState(
    typeof window === 'undefined' ? true : window.innerWidth > 900,
  )
  const [zoom, setZoom] = useState(1)
  const [selBox, setSelBox] = useState<SelectionBox | null>(null)
  const stageRef = useRef<StageHandle | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const camRef = useRef<HTMLInputElement | null>(null)
  const coarse = useCoarsePointer()


  const doc = useEditor((s) => s.doc)
  const selectedId = useEditor((s) => s.selectedId)
  const present = useEditor((s) => s.present)
  const setPresent = useEditor((s) => s.setPresent)
  const setPhoto = useEditor((s) => s.setPhoto)
  const setSaveState = useEditor((s) => s.setSaveState)
  const newDocument = useEditor((s) => s.newDocument)
  const loadDocument = useEditor((s) => s.loadDocument)
  const clearRuns = useEditor((s) => s.clearRuns)

  const onReady = useCallback((h: StageHandle) => {
    stageRef.current = h
  }, [])
  const onStats = useCallback((s: RenderStats) => setStats(s), [])
  const onZoomChange = useCallback((z: number) => setZoom(z), [])
  const onSelectionBox = useCallback((b: SelectionBox | null) => setSelBox(b), [])

  /* ---- autosave, so the indicator in the top bar tells the truth ---- */

  // Anything left in the old localStorage store gets rescued once, so a job
  // saved before this existed is not lost to the upgrade.
  useEffect(() => {
    migrateFromLocalStorage()
  }, [])

  /*
   * ARRIVING FROM THE LOGIN.
   *
   * lightsos.com verifies the password in a function, and on success sends
   * the person here with the access code in the fragment. This is the other
   * end of that: read it once, save it for this device, and strip it out of
   * the address bar immediately so it is not sitting in the URL to be
   * screenshotted, shoulder read or pasted into a chat.
   *
   * replaceState rather than assigning location.hash, because assigning would
   * push a history entry and leave the code one Back press away.
   *
   * Runs before anything asks whether there is a code, so a person who logged
   * in never sees a password box at all.
   */
  /*
   * THE ACCESS CODE IS READ IN lib/session.ts NOW, NOT HERE.
   *
   * This effect pulled the code out of the hash and then wiped the hash, which
   * was correct when a hash only ever carried #k=CODE. The site now sends the
   * sign-in tokens in the same hash, and this ran first: it took the code,
   * erased the tokens with it, and a contractor who had just typed his
   * password on the website was asked to type it again on the app.
   *
   * One reader, in one pass, before anything is destroyed.
   */

  /*
   * A NEW HOUSE EVERY TIME YOU OPEN IT, AND THE LAST ONE ONE TAP AWAY.
   *
   * This used to reopen the newest job automatically. That was solving a real
   * problem - a reload mid-quote came back to an empty screen and the work
   * looked lost - but it solved it by making the app open on the last
   * customer's house. Pulling into a new driveway and finding the previous
   * job already loaded is worse than the thing it was fixing, because it
   * happens on EVERY open rather than on a rare refresh.
   *
   * So the rule is now the simple one: opening the app starts a new mockup.
   * Nothing is lost, because every job is already saved under Your jobs, and
   * the most recent one is offered by name on the upload screen. One tap
   * carries on, and it is a tap you chose rather than a state you inherited.
   */
  const [resumable, setResumable] = useState<{
    id: string
    name: string
    /** How much is on it and when, so Continue names what it is resuming. */
    runs: number
    savedAt: string
  } | null>(null)
  /**
   * JOBS IS HOME ONCE THERE IS A SEASON IN HERE.
   *
   * The app opened on the upload screen, which is the right first thing to see
   * on the first day and the wrong thing on every day after. By October a
   * contractor opens this to look at who has not answered and what is owed, not
   * to start another house - and the house he does want to start is one press
   * away either way.
   *
   * A brand new account still gets the upload screen, because a job list with
   * nothing in it is not a home, it is a shrug. And New job flips straight to
   * the upload screen without going through the list.
   */
  const [startNew, setStartNew] = useState(false)
  /*
    WHICH OF THE TWO THINGS THE LEFT COLUMN IS SHOWING, and it starts on the
    controls rather than the list.

    I had this the wrong way round. Reading the reference prototype's own
    script rather than its screenshot: its category list is a PICKER that is
    hidden by default, opened by Add lights, and closed again the moment you
    pick one - and its color swatches only ever appear once you select
    something already on the photograph, which is what its hint line has been
    telling everybody all along.

    Making the list permanent therefore did something the reference never
    does: it put the color a click behind a screen that has no color on it.
    Our product is better than the reference here and it should stay that way,
    because we let you set the look BEFORE you draw, so the swatches are worth
    having on screen from the start. The list is one press away, on the link
    above them.

    Selecting something on the photograph still wins over both.
  */
  const [picking, setPicking] = useState(false)
  const [jobCount, setJobCount] = useState<number | null>(null)
  const lookedRef = useRef(false)
  useEffect(() => {
    if (lookedRef.current) return
    lookedRef.current = true
    ;(async () => {
      try {
        const all = await listProjects()
        setJobCount(all.length)
        // Only a job with work in it is worth offering. An empty one is noise.
        const newest = all.find((p) => p.layers > 0)
        if (newest)
          setResumable({
            id: newest.id,
            name: newest.name,
            runs: newest.layers,
            savedAt: newest.savedAt,
          })
      } catch {
        /* a blocked or unavailable database just means nothing to offer */
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * COMING BACK TO NO PHOTO MEANS COMING BACK HOME.
   *
   * startNew is a press, not a mode. Without this it stays true for the rest of
   * the session, so a contractor who starts one job at nine in the morning
   * never sees his job list again until he reloads the tab.
   *
   * The count is re-read at the same moment for the account that just saved its
   * first job: it booted with an empty list, and without a fresh read it would
   * keep being handed the upload screen over a season of work.
   */
  const hadPhoto = useRef(false)
  useEffect(() => {
    if (doc.photo) {
      hadPhoto.current = true
      setStartNew(false)
      return
    }
    if (!hadPhoto.current) return
    hadPhoto.current = false
    listProjects()
      .then((all) => setJobCount(all.length))
      .catch(() => {
        /* nothing to count is the same as nothing to show */
      })
  }, [doc.photo])

  // The thumbnail is built from the photo rather than the canvas: it only has
  // to be recognizable in a list, and this way it never waits on the renderer
  // and never gets rebuilt because a slider moved.
  const thumbRef = useRef<{ src: string; thumb?: string }>({ src: '' })
  useEffect(() => {
    const src = doc.photo?.dataUrl
    if (!src || thumbRef.current.src === src) return
    makeThumb(src).then((thumb) => {
      thumbRef.current = { src, thumb }
    })
  }, [doc.photo?.dataUrl])

  useEffect(() => {
    if (!doc.photo) return
    setSaveState('saving')
    const t = setTimeout(async () => {
      const res = await saveProject(doc, {
        thumb: thumbRef.current.thumb,
        // So the job list can show the document number without opening the job.
        prefix: useEditor.getState().company.prefix,
        /*
         * The job list used to carry the last quote total and footage so it
         * could show them without re-rendering forty houses. Neither is shown
         * while this is a visual design tool, so neither is written; a stored
         * figure nobody displays is a figure that goes stale and then reappears
         * looking authoritative. The fields stay on the summary type, and the
         * two lines come back with the price.
         */
      })
      setSaveState(res === 'saved' ? 'saved' : 'error')
      // Local first, then up. A failed push leaves the job dirty and the next
      // pass takes it, so nothing here blocks or warns: the work is already
      // safe on this machine before the network is involved at all.
      void syncNow(useAuth.getState().session?.user.id)
    }, 700)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // stats change on every render pass; the debounce is keyed on the document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc])

  /* ---- present mode ---- */
  useEffect(() => {
    if (!present) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPresent(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [present, setPresent])

  const fileName = () => `${doc.name.replace(/[^\w-]+/g, '-').toLowerCase() || 'mockup'}.jpg`

  const onExport = async () => {
    const h = stageRef.current
    if (!h) return
    setExporting(true)
    try {
      const blob = await h.exportBlob(1)
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName()
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
    } finally {
      setExporting(false)
    }
  }

  /*
   * SEND THE PICTURE, WHERE THE DEVICE CAN ACTUALLY SEND IT.
   *
   * Export on a phone or a tablet means texting the homeowner the picture
   * while standing in their driveway, and the share sheet is the only thing
   * that can do that from a web app. Offered only where the browser really
   * supports sharing a file - canShare with the file itself, not just the
   * presence of navigator.share, because iOS reports share and then refuses
   * files - and a download is always there underneath.
   */
  const [canShare, setCanShare] = useState(false)
  useEffect(() => {
    try {
      const probe = new File([new Blob([''], { type: 'image/jpeg' })], 'p.jpg', {
        type: 'image/jpeg',
      })
      setCanShare(!!navigator.canShare?.({ files: [probe] }))
    } catch {
      setCanShare(false)
    }
  }, [])

  const onShare = async () => {
    const h = stageRef.current
    if (!h) return
    setExporting(true)
    try {
      const blob = await h.exportBlob(1)
      if (!blob) return
      const file = new File([blob], fileName(), { type: 'image/jpeg' })
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: doc.name })
      } else {
        await onExport()
      }
    } catch {
      /* the person closed the share sheet, which is not a failure */
    } finally {
      setExporting(false)
    }
  }

  const pickFile = async (file: File | undefined) => {
    if (!file) return
    try {
      const p = await loadPhotoFile(file)
      setPhoto(p.dataUrl, p.width, p.height)
    } catch {
      /* handled in the panel */
    }
  }

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    await pickFile(e.dataTransfer.files?.[0])
  }

  const advanced = useEditor((s) => s.advanced)
  const session = useAuth((s) => s.session)
  const authReady = useAuth((s) => s.ready)
  const allowed = useAuth((s) => s.allowed)
  const bootAuth = useAuth((s) => s.boot)
  useEffect(() => {
    void bootAuth()
  }, [bootAuth])

  /*
   * SYNC ON THE THREE THINGS THAT ACTUALLY CHANGE THE ANSWER.
   *
   * Signing in, because that is when this browser's season becomes somebody's
   * and when another device's work is waiting. Coming back to the tab, because
   * a contractor quotes on a phone and then opens the laptop. And regaining a
   * connection, because the interesting case is the one where the last few
   * saves happened in a gated community with no signal.
   *
   * There is no timer. Polling a database every thirty seconds for a hundred
   * accounts costs real money to answer a question nobody asked.
   */
  const userId = session?.user.id
  useEffect(() => {
    if (!userId) return
    void syncNow(userId)
    const again = () => {
      if (document.visibilityState === 'visible') void syncNow(userId)
    }
    document.addEventListener('visibilitychange', again)
    window.addEventListener('online', again)
    return () => {
      document.removeEventListener('visibilitychange', again)
      window.removeEventListener('online', again)
    }
  }, [userId])
  const [showProjects, setShowProjects] = useState(false)
  const [showQuote, setShowQuote] = useState(false)
  /*
   * SHARE DESIGN IS THE EDITOR'S NEXT ACTION.
   *
   * Most people who open this app never write a quote: they photograph a
   * house, draw the lights, and text the homeowner the picture from the
   * driveway. Export and Quote sat side by side on the bar as two equal
   * choices, which made the common case one of two options rather than the
   * point. See ui/ShareSheet.tsx.
   */
  const [showShare, setShowShare] = useState(false)

  /*
   * WHAT ANYBODY KNOWS ABOUT THE HOUSE. See ui/MeasureSheet.tsx.
   */
  const [showMeasure, setShowMeasure] = useState(false)

  const fit = () => stageRef.current?.fit()
  const zoomBy = (f: number) => stageRef.current?.zoomBy(f)

  /*
   * THE FRONT DOOR, BEFORE ANYTHING ELSE RENDERS.
   *
   * Not a banner over a working app and not a redirect after the canvas has
   * already loaded somebody's last job. No session, no app. Rendering the
   * editor first and then covering it is how a screenshot of a stranger's
   * house ends up in a support ticket.
   *
   * `ready` is what stops this flashing the login form at somebody who IS
   * signed in: a session takes a moment to read back, and guessing during that
   * moment guesses wrong.
   */
  if (!authReady) return <div className="gate" />
  /*
   * OPEN MODE SKIPS THE DOOR. See lib/account.ts: the account is named by
   * VITE_OPEN_ACCOUNT instead of by a session, so there is nobody to ask for.
   * Unset the variable and the front door is back, unchanged.
   */
  if (!session && !OPEN_ACCOUNT) return <SignIn />

  /*
   * NOTHING UNTIL WE KNOW WHICH SCREEN THIS IS.
   *
   * jobCount is null until the offline database answers, and while it was null
   * neither the workspace test below nor the upload screen's test matched - so
   * the app rendered the EDITOR SHELL: a dark empty stage with a top bar and a
   * dock on it, for as long as reading thirty-seven jobs and their thumbnails
   * takes. On a laptop that is a flicker. On a phone it is long enough to
   * press something, and pressing Jobs in that window opens the sheet that
   * belongs over a job, on top of an editor holding no job at all. Reported
   * exactly that way: this is the first screen I see when I log in.
   *
   * The same held blank as the auth check above, for the same reason: showing
   * the wrong screen briefly is worse than showing nothing briefly.
   */
  if (jobCount === null && !doc.photo && !startNew) return <div className="gate" />

  /*
   * THE WORKSPACE COMES BEFORE THE EDITOR, not on top of it.
   *
   * This used to render the job list inside the stage, underneath the editor's
   * own top bar, which is how a light workspace ended up wearing a dark
   * header it has nothing to do with and a sidebar sitting under it.
   *
   * By October a contractor opens this to see who has not answered and what is
   * owed, not to start another house - and the house he does want to start is
   * one press away either way. A brand new account still goes straight to the
   * upload screen, because a job list with nothing in it is not a home, it is
   * a shrug.
   */
  if (!doc.photo && !startNew && jobCount !== null && jobCount > 0) {
    return (
      <div className="ws-root">
        {!allowed && <Lapsed />}
        <Workspace
          onStartNew={() => setStartNew(true)}
          onOpenQuote={() => setShowQuote(true)}
        />
        {/*
          THE QUOTE OPENS OVER THE WORKSPACE TOO.
          
          Opening a job's quote loads that document, and a job whose photograph
          the browser has evicted still has every line and every payment on it.
          Without this the sheet would only exist on the editor's side of the
          return above, so pricing exactly the jobs most likely to need looking
          at would do nothing at all.
        */}
        {showQuote && <Quote onClose={() => setShowQuote(false)} />}
      </div>
    )
  }

  return (
    <div className={present ? 'app presenting' : 'app'}>
      {/*
        A lapsed pass gets in and reads everything. Nothing is deleted and
        nothing is hidden: the season of work is the reason to come back, and
        those customers' details are his. He simply cannot make anything new,
        which the store enforces on the way into the document rather than by
        disabling buttons. See lib/editable.ts.
      */}
      {!allowed && <Lapsed />}
      {/*
        HOW LONG IS LEFT, over the photograph as well as over the workspace.
        Thin, quiet, and absent entirely for a paid account. It renders nothing
        while the access state is still being read, because a bar that guesses
        tells a paying contractor he is on a trial.
      */}
      {!present && <TrialNotice />}
      <TopBar
        /*
         * HOME IS THE JOB LIST. Dropping the photo is what puts the workspace
         * back on screen - see the hadPhoto effect above, which also clears
         * startNew, so the list appears rather than the upload screen.
         */
        /*
         * AND STARTING A DESIGN IS A PRESS, NOT A PLACE YOU GET STUCK IN.
         * Dropping the photo is enough to come home from a job that has one,
         * because the effect above clears startNew when a photo arrives. It
         * is not enough from the upload screen: no photo ever arrived there,
         * so newDocument had nothing to drop and the mark did nothing at all.
         * Reported exactly that way: clicking the icon does not go back.
         */
        onHome={() => {
          setStartNew(false)
          newDocument()
        }}
        onNew={() => {
          setStartNew(true)
          // No confirmation any more. It used to warn that the current mockup
          // would be discarded, which was true when the only copy lived in one
          // browser key. Jobs now save themselves and are listed under Your
          // jobs, so nothing is lost and the warning was just frightening
          // people out of starting the next one.
          newDocument()
        }}
        onClearRuns={clearRuns}
        hasRuns={doc.layers.length > 0}
        onFit={fit}
        onProjects={() => setShowProjects(true)}
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen((v) => !v)}
        onShare={onShare}
        onMeasure={() => setShowMeasure(true)}
        canShare={canShare}
        exporting={exporting}
        onQuote={() => setShowQuote(true)}
        onShareDesign={() => setShowShare(true)}
        onChangePhoto={() => fileRef.current?.click()}
      />

      {/*
        has-panel RESERVES THE SPACE THE PANEL SITS IN.

        In simple mode the stage is absolutely positioned across the whole
        body and the inspector floats on top of it at the top right. That was
        fine while the panel came and went with a selection and the photo
        happened to be letterboxed; it is not fine now that the panel is
        always there, because a wide photograph runs underneath it.

        So when the panel is showing, the stage stops short of it. The photo
        gets smaller rather than getting covered, which is the whole point,
        and StageView refits itself on the resize.
      */}
      <div
        className={[
          'body',
          advanced ? '' : 'no-nav',
          panelOpen ? '' : 'panel-closed',
          !advanced && doc.photo && !present ? 'has-panel' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/*
          In simple mode the categories live in the panel, so the nav is not a
          second place to look for the same thing. Removing it also gives the
          photo back a couple of hundred pixels, which matters more than any
          control on a laptop screen.
        */}
        {advanced && <LeftNav onFit={fit} />}

        <div
          className={dragging ? 'stage-wrap dropping' : 'stage-wrap'}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <StageView
            onStats={onStats}
            onReady={onReady}
            onZoomChange={onZoomChange}
            onSelectionBox={onSelectionBox}
          />

          {/*
            WHAT YOU ARE LOOKING AT, at the top left of the card, as the
            reference has it. It says the one thing about the picture that a
            person cannot tell from the picture on a bright screen: whether
            this is the night render or the daylight photograph.
          */}
          {doc.photo && !present && (
            <p className="stage-label">{doc.scene.night > 0.01 ? 'Night' : 'Daylight'}</p>
          )}

          {/*
            THE TOOLBAR BELONGS TO THE PHOTOGRAPH.

            It was a sibling of the stage, inside the body, so it positioned
            itself against the body rather than the picture. On a desktop the
            two happen to coincide and it looked right; on a phone the body is
            the whole column, so the bar floated across the middle of the
            category list and covered three of the nine things it exists to
            let you add. The reference has it inside the viewport, which is
            also just true: it is the picture's bar.
          */}
          {/*
            THE DOCK IS ON THE PICTURE IN BOTH VIEWS.

            It used to disappear in advanced, which is what turned that flag
            from extra panels into a different application: night and day, Add,
            and the way back all left the screen at once, and the only thing
            left saying how to get them back was a menu item three dots away
            reading Simple view. The rails can come and go. The bar on the
            picture stays.
          */}
          <Dock
            onExport={onExport}
            exporting={exporting}
            onShare={onShare}
            canShare={canShare}
          />

          <CanvasControls zoom={zoom} onZoom={zoomBy} onFit={fit} />
          <DrawHud />
          <SelectionBadge box={selBox} metrics={stats.perStrand} />


          {!doc.photo && (startNew || jobCount === 0) && (
            <div className="empty">
              <div className="empty-inner">
                {/*
                  A JOB CAN OUTLIVE ITS PHOTOGRAPH.

                  If the image write failed or the browser evicted it, the
                  design is still all here - every run is stored in normalized
                  coordinates - and dropping the same photo back in puts it
                  exactly where it was. Saying so is the difference between a
                  recoverable afternoon and a job drawn twice.
                */}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
                {/*
                  A SECOND INPUT, AND THE ONLY DIFFERENCE IS capture.
                  It opens the camera rather than a chooser, which is the whole
                  job on a phone. One input with capture on it would take the
                  library away, and the photo taken last October is a real way
                  to start a job in July.
                */}
                <input
                  ref={camRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />

                {/*
                  THE HEADING LIVES INSIDE THE TARGET.
                  It used to sit above a separate dashed box that repeated the
                  instruction in smaller words, so the screen said start with a
                  photo, then drop a photo here, then choose a file: three
                  imperatives for one act. One panel, one sentence, and the two
                  ways past it side by side.

                  A DASHED EDGE IS A PROMISE THAT SOMETHING CAN BE DROPPED ON
                  IT, so it only exists where something can be. On a phone the
                  whole act is pressing a button and pointing the camera at a
                  house, and a dashed rectangle around that button is
                  decoration pretending to be an affordance.
                */}
                <div className={dragging ? 'dz over' : 'dz'}>
                  <h2>Start with a photo</h2>
                  <p>
                    {coarse
                      ? 'Stand across the street and photograph the front of the house.'
                      : 'Upload a clear front-facing photo of the home. Drop a photo here.'}
                  </p>
                  <div className="dz-btns">
                    {coarse ? (
                      <button className="btn primary" onClick={() => camRef.current?.click()}>
                        <IconPhoto size={19} />
                        Take a photo
                      </button>
                    ) : (
                      <button className="btn primary" onClick={() => fileRef.current?.click()}>
                        <IconPhoto size={19} />
                        Choose a file
                      </button>
                    )}
                    {/*
                      THE ONLY WAY IN WITH NO HOUSE IN FRONT OF YOU. It was
                      four words of gray text with no edge on them, under a
                      rule, at the bottom. Somebody deciding whether to buy
                      this opens it at a desk, where there is nothing to
                      photograph, and the one control that shows them what the
                      product does was styled to be skipped.
                    */}
                    <button
                      className="btn"
                      onClick={() => {
                        const p = makeDemoPhoto()
                        setPhoto(p.dataUrl, p.width, p.height)
                      }}
                    >
                      See a sample house
                    </button>
                  </div>
                  {coarse && (
                    <button className="link-btn dz-alt" onClick={() => fileRef.current?.click()}>
                      Choose one you already have
                    </button>
                  )}
                </div>

                <div className="dz-tips">
                  <div className="card pad">
                    <b>Daytime photo</b>
                    <small>Natural light works best.</small>
                  </div>
                  <div className="card pad">
                    <b>Straight-on angle</b>
                    <small>Stand across the street if possible.</small>
                  </div>
                  <div className="card pad">
                    <b>Keep the phone level</b>
                    {/* It used to say hold the phone straight, which is the
                        heading again in different words. */}
                    <small>Level with the ground, not tilted up at the roof.</small>
                  </div>
                </div>

                {resumable && (
                  <div className="card pad dz-cont">
                    <div>
                      <b>Continue {resumable.name}</b>
                      <small>
                        {resumable.runs} {resumable.runs === 1 ? 'run' : 'runs'} drawn, saved{' '}
                        {SHORT.format(new Date(resumable.savedAt))}
                      </small>
                    </div>
                    <button
                      className="btn"
                      onClick={() => {
                        void (async () => {
                          const d = await loadProject(resumable.id)
                          // Runs with no photo behind them is not a job you can
                          // work on, so a job whose picture cannot be read is
                          // left alone rather than half opened.
                          if (d?.photo) loadDocument(d)
                        })()
                      }}
                    >
                      Continue
                    </button>
                  </div>
                )}

                {/*
                  A JOB CAN OUTLIVE ITS PHOTOGRAPH, AND THAT IS A NOTICE, NOT A
                  DIFFERENT SCREEN.

                  It used to replace the heading, so a person whose browser had
                  evicted one image was shown a screen that no longer said what
                  it was for. If the image write failed or the browser evicted
                  it, the design is still all here, every run stored in
                  normalized coordinates, and dropping the same photo back in
                  puts it exactly where it was. Saying so is the difference
                  between a recoverable afternoon and a job drawn twice.
                */}
                {doc.layers.length > 0 && (
                  <p className="dz-lost">
                    <b>This job lost its photo.</b> The design is still here, all{' '}
                    {doc.layers.length} {doc.layers.length === 1 ? 'run' : 'runs'} of it. Adding the
                    same photo again puts it back exactly where it was.
                  </p>
                )}
              </div>
            </div>
          )}

          {present && (
            <button className="present-exit" onClick={() => setPresent(false)} title="Close (Esc)">
              <IconClose size={18} />
            </button>
          )}
        </div>

        {/*
          ONE PANEL, AND ONLY WHEN THERE IS SOMETHING IN IT.

          Advanced still has its rail, because it is the workshop. Simple mode
          has the photograph, a bar of four actions along the bottom and an
          inspector that appears when a run is selected. There is deliberately
          no second panel competing with it.
        */}
        {advanced ? (
          <Rail stats={stats} onExport={onExport} exporting={exporting} />
        ) : (
          <>
            {/*
              THE LEFT COLUMN IS EITHER THE LIST OR THE CONTROLS, as the
              reference prototype has it. Nothing selected and nothing picked
              shows what you can put on a house; picking one, or selecting
              something already on the photograph, shows its controls.

              The nine things were behind Add in the toolbar, which means
              somebody opening the editor for the first time could not see what
              the product does without pressing something to find out.
            */}
            {/*
              AND NOTHING IN THE LEFT COLUMN UNTIL THERE IS A HOUSE.

              The upload screen was showing the full list of what you can put
              on a house beside a card asking for a photograph of one. Nine
              things you cannot do yet, taking a quarter of the window, with
              the one thing you can do floating in the gray beside them.
            */}
            {doc.photo &&
              (picking && !selectedId ? (
                <CategoryRail onPick={() => setPicking(false)} onClose={() => setPicking(false)} />
              ) : (
                <Inspector onBack={() => setPicking(true)} />
              ))}
          </>
        )}
      </div>

      {showProjects && (
        <Projects
          onClose={() => setShowProjects(false)}
          // New job from the sheet means the upload screen, not the same list
          // again underneath it.
          onStartNew={() => setStartNew(true)}
        />
      )}
      {showShare && (
        <ShareSheet
          render={() => stageRef.current?.exportBlob(1) ?? Promise.resolve(null)}
          fileName={fileName()}
          canShare={canShare}
          onQuote={() => {
            setShowShare(false)
            setShowQuote(true)
          }}
          onClose={() => setShowShare(false)}
        />
      )}
      {showMeasure && (
        <MeasureSheet stats={stats.perStrand} onClose={() => setShowMeasure(false)} />
      )}
      {showQuote && <Quote onClose={() => setShowQuote(false)} />}

      {/*
        Developer tuning for the night grade. Behind Advanced because it is a
        photographer's panel, not a contractor's - but live, because the whole
        point of moving the grade onto the GPU was that finding the right
        number should cost a drag rather than a rebuild.
      */}
      {advanced && <NightDebug night={() => stageRef.current?.night() ?? null} />}
    </div>
  )
}
